import React from 'react'
import { X, Calendar, User, DollarSign, FileText, MapPin } from 'lucide-react'

const ViewPrePaymentModal = ({ isOpen, onClose, prePayment }) => {
  const formatMonth = (monthStr) => {
    if (!monthStr) return 'N/A'
    
    // Handle both date objects and YYYY-MM strings
    const date = new Date(monthStr)
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      // Try parsing as YYYY-MM format
      const dateFromYearMonth = new Date(monthStr + '-01')
      if (isNaN(dateFromYearMonth.getTime())) {
        return 'N/A'
      }
      return dateFromYearMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    }
    
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
  }

  const formatCurrency = (amount) => {
    if (!amount) return '0.00'
    return parseFloat(amount).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A'
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (!isOpen || !prePayment) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header - Fixed */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900">Pre Payment Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Employee and Month Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Employee */}
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <User className="h-5 w-5 text-blue-600" />
                  </div>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">Employee</p>
                  <p className="text-sm text-gray-600 mt-1">
                    {prePayment.employee?.fullName || 'N/A'}
                  </p>
                </div>
              </div>

              {/* Month */}
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <Calendar className="h-5 w-5 text-green-600" />
                  </div>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">Month</p>
                  <p className="text-sm text-gray-600 mt-1">
                    {formatMonth(prePayment.month)}
                  </p>
                </div>
              </div>
            </div>

            {/* Amount and Branch */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Amount */}
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <DollarSign className="h-5 w-5 text-orange-600" />
                  </div>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">Amount</p>
                  <p className="text-lg font-semibold text-gray-900 mt-1">
                    ${formatCurrency(prePayment.amount)}
                  </p>
                </div>
              </div>

              {/* Branch */}
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <MapPin className="h-5 w-5 text-purple-600" />
                  </div>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">Branch</p>
                  <p className="text-sm text-gray-600 mt-1">
                    {prePayment.branch?.name || prePayment.employee?.branch?.name || 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {/* Note */}
            {prePayment.note && (
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                    <FileText className="h-5 w-5 text-gray-600" />
                  </div>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">Note</p>
                  <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">
                    {prePayment.note}
                  </p>
                </div>
              </div>
            )}

            {/* Timestamps */}
            <div className="border-t border-gray-200 pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-gray-500">
                <div>
                  <p className="font-medium">Created</p>
                  <p className="mt-1">{formatDate(prePayment.createdAt)}</p>
                </div>
                <div>
                  <p className="font-medium">Last Updated</p>
                  <p className="mt-1">{formatDate(prePayment.updatedAt)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer - Fixed */}
        <div className="flex justify-end p-6 border-t border-gray-200 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default ViewPrePaymentModal
